import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../styles.css';
import Header from './Header';
import LoadingSpinner from './LoadingSpinner';
import NoteCard from './NoteCard';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { handleNoteCreation } from '../utils/noteHandlers';
import { handleNoteClick } from '../utils/navigationHandlers';

const MONTH_NAMES = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

function HomePage({ handleLogout }) {
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [notes, setNotes] = useState([]);
  const [isStatsLoading, setIsStatsLoading] = useState(false);
  const [isNotesLoading, setIsNotesLoading] = useState(false);
  const [hasNotesInitialized, setHasNotesInitialized] = useState(false);
  const [expandedMonthKeys, setExpandedMonthKeys] = useState([]);
  const navigate = useNavigate();

  const fetchMonthlyStats = async () => {
    setIsStatsLoading(true);
    try {
      const response = await fetch('/api/monthly-stats', {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Erreur lors de la récupération des statistiques');

      const data = await response.json();
      setMonthlyStats(data);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setIsStatsLoading(false);
    }
  };

  const fetchNotes = async (signal) => {
    let loadingTimeout;

    try {
      loadingTimeout = setTimeout(() => {
        if (!signal?.aborted) {
          setIsNotesLoading(true);
        }
      }, 500);

      const response = await fetch('/api/get-notes', {
        method: 'GET',
        credentials: 'include',
        signal,
      });

      if (!response.ok) throw new Error('Erreur lors de la récupération des notes');

      const data = await response.json();
      setNotes(data.notes || []);
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Erreur:', error);
    } finally {
      clearTimeout(loadingTimeout);
      setIsNotesLoading(false);
      setHasNotesInitialized(true);
    }
  };

  const getMonthInfo = useCallback((note) => {
    const rawMonth = note?.mois;
    const rawYear = note?.annee;

    let monthNumber = null;
    let yearNumber = Number(rawYear) || null;

    if (typeof rawMonth === 'number') {
      monthNumber = rawMonth;
    } else if (typeof rawMonth === 'string') {
      if (/^\d{1,2}$/.test(rawMonth.trim())) {
        monthNumber = Number(rawMonth.trim());
      } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(rawMonth.trim())) {
        const [, month, year] = rawMonth.trim().split('/');
        monthNumber = Number(month);
        yearNumber = Number(year);
      } else {
        const parsedDate = new Date(rawMonth);
        if (!Number.isNaN(parsedDate.getTime())) {
          monthNumber = parsedDate.getMonth() + 1;
          yearNumber = parsedDate.getFullYear();
        }
      }
    }

    if (!monthNumber || monthNumber < 1 || monthNumber > 12) {
      monthNumber = 1;
    }

    if (!yearNumber) {
      yearNumber = new Date().getFullYear();
    }

    return {
      monthNumber,
      yearNumber,
      label: `${MONTH_NAMES[monthNumber - 1]} ${yearNumber}`,
      key: `${yearNumber}-${String(monthNumber).padStart(2, '0')}`
    };
  }, []);

  const groupedNotes = useMemo(() => {
    const groups = new Map();

    notes.forEach((note) => {
      const monthInfo = getMonthInfo(note);
      if (!groups.has(monthInfo.key)) {
        groups.set(monthInfo.key, {
          ...monthInfo,
          notes: []
        });
      }
      groups.get(monthInfo.key).notes.push(note);
    });

    return Array.from(groups.values()).sort((a, b) => {
      if (a.yearNumber !== b.yearNumber) return b.yearNumber - a.yearNumber;
      return b.monthNumber - a.monthNumber;
    });
  }, [getMonthInfo, notes]);


  useEffect(() => {
    setExpandedMonthKeys((currentKeys) => {
      if (currentKeys.length > 0 || groupedNotes.length === 0) {
        return currentKeys;
      }

      return [groupedNotes[0].key];
    });
  }, [groupedNotes]);

  const toggleMonthGroup = (monthKey) => {
    setExpandedMonthKeys((currentKeys) =>
      currentKeys.includes(monthKey)
        ? currentKeys.filter((key) => key !== monthKey)
        : [...currentKeys, monthKey]
    );
  };

  const askForNoteTitle = async () => {
    const result = await Swal.fire({
      title: 'Nouvelle note de frais',
      input: 'text',
      inputLabel: 'Titre de la note',
      inputPlaceholder: 'Exemple : Avril 2026',
      showCancelButton: true,
      confirmButtonText: 'Créer',
      cancelButtonText: 'Annuler',
      inputValidator: (value) => {
        if (!value || !value.trim()) {
          return 'Veuillez saisir un titre pour la note.';
        }
        return null;
      },
      customClass: {
        popup: 'note-title-popup',
        title: 'note-title-heading',
        inputLabel: 'note-title-label',
        input: 'note-title-input',
        confirmButton: 'custom-confirm-button',
        cancelButton: 'custom-cancel-button'
      }
    });

    if (result.isConfirmed) {
      await handleNoteCreation(result.value.trim(), navigate);
    }
  };

  useEffect(() => {
    const abortController = new AbortController();
    fetchMonthlyStats();
    fetchNotes(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, []);

  return (
    <div>
      <main>
        <div className="homepage-container">
          <div className="monthly-recap">
            <h2>Récap du mois</h2>
            {isStatsLoading ? (
              <LoadingSpinner message="Chargement des statistiques..." />
            ) : monthlyStats ? (
              <div className="stats-text">
                <div className="stat-item">
                  <strong>Argent dépensé ce mois-ci :</strong> {monthlyStats.totalExpenses} €
                </div>
                
                <div className="stat-item">
                  <strong>Catégorie de dépense principale :</strong> {monthlyStats.highestExpenseType || 'Aucune'}
                </div>
              </div>
            ) : (
              <div className="stats-text">
                <div className="stat-item">
                  <strong>Argent dépensé ce mois-ci :</strong> 0,00 €
                </div>
                <div className="stat-item">
                  <strong>Catégorie de dépense principale :</strong> Aucune
                </div>
              </div>
            )}
          </div>

          <section className="notes-months-section">
            <div className="notes-months-header">
              <h2>Notes de frais</h2>
              {groupedNotes.length > 0 && (
                <span className="notes-months-count">{notes.length} note{notes.length > 1 ? 's' : ''}</span>
              )}
            </div>

            {isNotesLoading ? (
              <LoadingSpinner message="Chargement des notes de frais..." />
            ) : !hasNotesInitialized ? null : groupedNotes.length > 0 ? (
              <div className="month-groups">
                {groupedNotes.map((group) => {
                  const isExpanded = expandedMonthKeys.includes(group.key);

                  return (
                    <section key={group.key} className={`month-group ${isExpanded ? 'expanded' : ''}`}>
                      <button
                        type="button"
                        className="month-toggle"
                        onClick={() => toggleMonthGroup(group.key)}
                        aria-expanded={isExpanded}
                      >
                        <div className="month-toggle-text">
                          <span className="month-toggle-title">{group.label}</span>
                          <span className="month-toggle-subtitle">
                            {group.notes.length} note{group.notes.length > 1 ? 's' : ''}
                          </span>
                        </div>
                        <i className={`fa-solid fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                      </button>

                      {isExpanded && (
                        <div className="month-notes">
                          {group.notes.map((note) => (
                            <NoteCard
                              key={note.id_note}
                              note={note}
                              onDelete={fetchNotes}
                              handleNoteClick={() => handleNoteClick(note.id_note, navigate)}
                            />
                          ))}
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">
                  <i className="fa-solid fa-clipboard-list"></i>
                </div>
                <h3>Aucune note de frais</h3>
                <p>Créez votre première note pour commencer à suivre vos dépenses.</p>
              </div>
            )}
          </section>
        </div>
      </main>

      <footer>
        <Header handleLogout={handleLogout} />
        <button
          className="footer-center-button footer-submit-button"
          onClick={askForNoteTitle}
          aria-label="Ajouter une note de frais"
          title="Ajouter une note de frais"
        >
          <i className="fa-solid fa-plus"></i>
        </button>
      </footer>
    </div>
  );
}

export default HomePage;
